import React, { useState, useEffect } from 'react';
import {
  X, User, Phone, Mail, MapPin, Building2, Tag, StickyNote,
  Calendar, ChevronRight, TrendingUp, Save, Loader2, CheckCircle2, UserPlus,
} from 'lucide-react';
import { avatarColor, initials, readLocalObject, writeLocal, NOTES_KEY } from './utils';

const Row = ({ icon: Icon, label, value, testid }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      <Icon size={14} className="text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{label}</p>
        <p className="text-sm text-gray-800 break-words">{value}</p>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="px-4 py-3 border-b border-gray-100">
    <h4 className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-2">{title}</h4>
    {children}
  </div>
);

const ProfilePanel = ({
  open, onClose, selectedChat, contactInfo,
  leadDetails, propertyDetails, loadingDetails, labelsMeta, onCreateLead,
}) => {
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [savedPulse, setSavedPulse] = useState(false);

  const phone = selectedChat?.phone;

  useEffect(() => {
    if (!phone) return;
    const all = readLocalObject(NOTES_KEY);
    setNotes(all[phone] || '');
    setNotesDirty(false);
  }, [phone]);

  const saveNotes = () => {
    if (!phone) return;
    const all = readLocalObject(NOTES_KEY);
    all[phone] = notes;
    writeLocal(NOTES_KEY, all);
    setNotesDirty(false);
    setSavedPulse(true);
    setTimeout(() => setSavedPulse(false), 1400);
  };

  if (!open || !selectedChat) return null;

  const name = selectedChat.recipientName || selectedChat.phone;
  const lead = leadDetails;
  const prop = propertyDetails;

  // Labels rendering — resolve ids to label meta (name, color)
  const labelIds = (lead?.labels || prop?.labels || []).filter(Boolean);
  const resolvedLabels = labelIds.map(id => {
    const meta = (labelsMeta || []).find(l => l.id === id);
    return meta || { id, name: id, color: '#e5e7eb' };
  });

  // Last booking / last deal heuristic — show latest from lead tasks / property
  const lastDeal = lead ? {
    stage: lead.current_stage || lead.stage || '—',
    value: lead.deal_value || lead.value,
    status: lead.status || lead.property_status,
    updatedAt: lead.updatedAt || lead.createdAt,
  } : null;

  const lastBooking = prop ? {
    propertyName: prop.propertyName || prop.hotel_name || '',
    city: prop.city,
    status: prop.status,
    onboardedAt: prop.onboardedAt || prop.updatedAt,
  } : null;

  return (
    <aside
      className="fixed md:static inset-0 md:inset-auto z-30 md:z-auto w-full md:w-[340px] lg:w-[360px] shrink-0 bg-white border-l border-gray-200 flex flex-col shadow-xl md:shadow-none"
      data-testid="inbox-profile-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 text-sm">Contact Info</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="inbox-profile-close">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Avatar block */}
        <div className="py-6 flex flex-col items-center border-b border-gray-100">
          <div className={`h-20 w-20 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-inner ${avatarColor(selectedChat.phone)}`}>
            {initials(name, selectedChat.phone)}
          </div>
          <h2 className="mt-3 text-lg font-semibold text-gray-900 text-center px-4 truncate max-w-full">{name}</h2>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1"><Phone size={12} />{selectedChat.phone}</p>
          {contactInfo && (
            <span className={`mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full
              ${contactInfo.type === 'lead' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
              {contactInfo.type.toUpperCase()}
            </span>
          )}
          {!contactInfo && !loadingDetails && (
            <span className="mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              UNKNOWN CONTACT
            </span>
          )}
        </div>

        {/* Unknown contact → offer quick-create lead */}
        {!contactInfo && !loadingDetails && onCreateLead && (
          <div className="mx-4 my-3 p-3 rounded-xl border border-dashed border-amber-300 bg-amber-50" data-testid="inbox-unknown-banner">
            <p className="text-xs text-amber-800 leading-relaxed">
              This number isn't linked to any lead or property. Create a lead to track follow-ups, notes and workflows.
            </p>
            <button
              onClick={onCreateLead}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
              data-testid="inbox-create-lead-btn"
            >
              <UserPlus size={14} /> Create Lead from this chat
            </button>
          </div>
        )}

        {loadingDetails && (
          <div className="flex justify-center py-6">
            <Loader2 className="animate-spin text-gray-400" size={18} />
          </div>
        )}

        {/* Contact details */}
        <Section title="Contact Details">
          <Row icon={User} label="Contact Name" value={lead?.owner_name || prop?.ownerName || name} testid="profile-contact-name" />
          <Row icon={Mail} label="Email" value={lead?.email || prop?.email} testid="profile-email" />
          <Row icon={Phone} label="Phone" value={selectedChat.phone} testid="profile-phone" />
          <Row icon={MapPin} label="City" value={lead?.city || prop?.city} testid="profile-city" />
        </Section>

        {/* Lead Info */}
        {lead && (
          <Section title="Lead Info">
            <Row icon={Building2} label="Hotel / Property" value={lead.hotel_name} testid="profile-hotel-name" />
            <Row icon={TrendingUp} label="Stage" value={lead.current_stage || lead.stage} testid="profile-stage" />
            <Row icon={CheckCircle2} label="Status" value={lead.property_status || lead.status} testid="profile-status" />
            <Row icon={Tag} label="Source" value={lead.source} testid="profile-source" />
          </Section>
        )}

        {/* Property Info */}
        {prop && !lead && (
          <Section title="Property Info">
            <Row icon={Building2} label="Property" value={prop.propertyName} testid="profile-property-name" />
            <Row icon={MapPin} label="Location" value={[prop.city, prop.state].filter(Boolean).join(', ')} testid="profile-property-location" />
            <Row icon={CheckCircle2} label="Status" value={prop.status} testid="profile-property-status" />
          </Section>
        )}

        {/* Labels */}
        <Section title="Labels">
          {resolvedLabels.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No labels assigned</p>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="profile-labels">
              {resolvedLabels.map(l => (
                <span key={l.id}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full text-gray-700"
                  style={{ backgroundColor: (l.color || '#e5e7eb') + '30', border: `1px solid ${l.color || '#e5e7eb'}` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: l.color || '#9ca3af' }} />
                  {l.name}
                </span>
              ))}
            </div>
          )}
        </Section>

        {/* Last Deal / Booking */}
        {(lastDeal || lastBooking) && (
          <Section title={lastDeal ? 'Last Deal' : 'Last Booking'}>
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl p-3" data-testid="profile-last-activity">
              {lastDeal && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Stage</span>
                    <span className="text-sm font-semibold text-gray-800">{lastDeal.stage}</span>
                  </div>
                  {lastDeal.value != null && (
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">Value</span>
                      <span className="text-sm font-semibold text-emerald-600">₹{Number(lastDeal.value).toLocaleString()}</span>
                    </div>
                  )}
                  {lastDeal.updatedAt && (
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-2">
                      <Calendar size={10} />
                      Updated {new Date(lastDeal.updatedAt).toLocaleDateString()}
                    </div>
                  )}
                </>
              )}
              {lastBooking && !lastDeal && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Property</span>
                    <span className="text-sm font-semibold text-gray-800 truncate">{lastBooking.propertyName}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">Status</span>
                    <span className="text-sm font-semibold text-blue-600">{lastBooking.status}</span>
                  </div>
                  {lastBooking.onboardedAt && (
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-2">
                      <Calendar size={10} />
                      {new Date(lastBooking.onboardedAt).toLocaleDateString()}
                    </div>
                  )}
                </>
              )}
              {lead?.id && (
                <a href={`/leads/${lead.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700">
                  Open in Leads <ChevronRight size={12} />
                </a>
              )}
            </div>
          </Section>
        )}

        {/* Notes */}
        <Section title="Notes">
          <textarea
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
            placeholder="Add private notes about this contact..."
            rows={4}
            className="w-full text-sm text-gray-800 p-2.5 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
            data-testid="inbox-profile-notes-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-gray-400 flex items-center gap-1">
              <StickyNote size={11} />
              {savedPulse ? <span className="text-emerald-600 font-medium">Saved</span> : 'Stored locally'}
            </span>
            <button
              onClick={saveNotes}
              disabled={!notesDirty}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="inbox-profile-save-notes"
            >
              <Save size={12} /> Save
            </button>
          </div>
        </Section>

        <div className="h-4" />
      </div>
    </aside>
  );
};

export default ProfilePanel;
